import { DomainError } from '../errors/domain-error';

export const MarketingAutomationStatus = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const;

export type MarketingAutomationStatusValue =
  (typeof MarketingAutomationStatus)[keyof typeof MarketingAutomationStatus];

export const MARKETING_AUTOMATION_STATUS_VALUES = Object.values(MarketingAutomationStatus);

export const MarketingAutomationTrigger = {
  FORM_JOIN: 'form_join',
  TAG_ADDED: 'tag_added',
  COURSE_PURCHASE: 'course_purchase',
  COURSE_ENROLLMENT: 'course_enrollment',
  MEMBERSHIP_SUBSCRIBED: 'membership_subscribed',
  CIRCLE_JOINED: 'circle_joined',
  CIRCLE_LEFT: 'circle_left',
  INACTIVITY: 'inactivity',
} as const;

export type MarketingAutomationTriggerValue =
  (typeof MarketingAutomationTrigger)[keyof typeof MarketingAutomationTrigger];

export const MARKETING_AUTOMATION_TRIGGER_VALUES = Object.values(MarketingAutomationTrigger);

export const MarketingAutomationActionType = {
  ADD_TAG: 'add_tag',
  ENROLL_SEQUENCE: 'enroll_sequence',
} as const;

export type MarketingAutomationActionTypeValue =
  (typeof MarketingAutomationActionType)[keyof typeof MarketingAutomationActionType];

export const MARKETING_AUTOMATION_ACTION_TYPE_VALUES = Object.values(
  MarketingAutomationActionType,
);

export type MarketingAutomationAction =
  | { type: 'add_tag'; tagName: string }
  | { type: 'enroll_sequence'; sequenceId: string };

/** Default win-back inactivity window (days). */
export const MARKETING_WINBACK_DEFAULT_INACTIVITY_DAYS = 30;

export function assertMarketingAutomationStatus(value: string): MarketingAutomationStatusValue {
  if (!(MARKETING_AUTOMATION_STATUS_VALUES as string[]).includes(value)) {
    throw new DomainError( 'Invalid automation status');
  }
  return value as MarketingAutomationStatusValue;
}

export function assertMarketingAutomationTrigger(
  value: string,
): MarketingAutomationTriggerValue {
  if (!(MARKETING_AUTOMATION_TRIGGER_VALUES as string[]).includes(value)) {
    throw new DomainError( 'Invalid automation trigger');
  }
  return value as MarketingAutomationTriggerValue;
}

export function assertMarketingInactivityDays(value: number): number {
  if (!Number.isInteger(value) || value < 1 || value > 365) {
    throw new DomainError( 'Inactivity days must be 1–365');
  }
  return value;
}

export function normalizeMarketingAutomationActions(raw: unknown): MarketingAutomationAction[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new DomainError( 'Add at least one automation action');
  }
  if (raw.length > 10) {
    throw new DomainError( 'Maximum 10 actions per automation');
  }
  return raw.map((item, index) => {
    if (!item || typeof item !== 'object') {
      throw new DomainError( `Action ${index + 1} is invalid`);
    }
    const row = item as Record<string, unknown>;
    const type = String(row.type ?? '');
    if (type === MarketingAutomationActionType.ADD_TAG) {
      const tagName = String(row.tagName ?? '').trim();
      if (!tagName) throw new DomainError( `Action ${index + 1} needs a tag name`);
      return { type: 'add_tag', tagName: tagName.slice(0, 80) };
    }
    if (type === MarketingAutomationActionType.ENROLL_SEQUENCE) {
      const sequenceId = String(row.sequenceId ?? '').trim();
      if (!sequenceId) {
        throw new DomainError( `Action ${index + 1} needs a sequence`);
      }
      return { type: 'enroll_sequence', sequenceId };
    }
    throw new DomainError( `Action type "${type}" is not allowed`);
  });
}

function packBlockId(): string {
  return `blk_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export function buildMarketingCtaStepBlocks(input: {
  heading: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
}) {
  return [
    { id: packBlockId(), type: 'heading' as const, level: 2 as const, text: input.heading },
    { id: packBlockId(), type: 'paragraph' as const, text: input.body },
    {
      id: packBlockId(),
      type: 'button' as const,
      label: input.ctaLabel,
      url: input.ctaUrl,
    },
  ];
}

/** Win-back + buyer-nurture pack copy (CTA deep-links to courses/offers). */
export function marketingWinBackSequenceSteps(input?: {
  courseCtaUrl?: string | null;
  offerCtaUrl?: string | null;
}) {
  const courseUrl = input?.courseCtaUrl?.trim() || 'https://loamify.io/discover';
  const offerUrl = input?.offerCtaUrl?.trim() || courseUrl;
  return [
    {
      subject: 'We miss you',
      delayMinutes: 0,
      heading: 'We miss you',
      body: 'It’s been a while — here’s what’s new. Tap below to jump back in.',
      ctaLabel: 'Explore courses',
      ctaUrl: courseUrl,
    },
    {
      subject: 'One more thing before you go quiet',
      delayMinutes: 3 * 24 * 60,
      heading: 'Still here when you’re ready',
      body: 'If now’s a better time, pick up where you left off with a fresh offer.',
      ctaLabel: 'See offers',
      ctaUrl: offerUrl,
    },
  ] as const;
}

export function marketingBuyerNurtureSequenceSteps(input?: {
  courseCtaUrl?: string | null;
  offerCtaUrl?: string | null;
}) {
  const courseUrl = input?.courseCtaUrl?.trim() || 'https://loamify.io/discover';
  const offerUrl = input?.offerCtaUrl?.trim() || courseUrl;
  return [
    {
      subject: 'Thanks for your purchase',
      delayMinutes: 0,
      heading: 'You’re in',
      body: 'Thanks for buying — here’s where to start, plus related courses and offers.',
      ctaLabel: 'Open your course',
      ctaUrl: courseUrl,
    },
    {
      subject: 'A next step you might like',
      delayMinutes: 2 * 24 * 60,
      heading: 'Keep the momentum',
      body: 'Members often continue with this next offer when they’re ready.',
      ctaLabel: 'See related offers',
      ctaUrl: offerUrl,
    },
  ] as const;
}

export const MARKETING_PACK_SEQUENCE_WINBACK = 'Win-back (pack)';
export const MARKETING_PACK_SEQUENCE_BUYER = 'Buyer nurture (pack)';
export const MARKETING_PACK_AUTOMATION_WINBACK = 'Inactivity → win-back (pack)';
export const MARKETING_PACK_AUTOMATION_BUYER = 'Course purchase → buyer nurture (pack)';
